import {
	type InfiniteData,
	type QueryClient,
	useInfiniteQuery,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query"
import type {
	ChatMessageDTO,
	GetChatMessagesResponse,
	PostChatMessageResponse,
	ProjectDTO,
	PropertyDTO,
	RealtimeMessage,
} from "@zplanem/responses"
import { useCallback } from "react"
import { apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { setMareczekThinking } from "./use-mareczek-thinking"

type ChatState =
	| { status: "loading" }
	| { status: "error"; message: string }
	| {
			status: "ready"
			messages: ChatMessageDTO[]
			threadId: string | null
	  }

/** Resolved @-mention picks for a send (ZPL-145); server re-validates. */
interface SendMentions {
	userIds: string[]
	mareczek: boolean
}

interface UseChatResult {
	state: ChatState
	sendMessage: (content: string, mentions?: SendMentions) => Promise<void>
	/** Fetch the next (older) page; no-op while one is already loading. */
	loadOlder: () => void
	hasOlder: boolean
	loadingOlder: boolean
}

/**
 * Prepend a new message into the FIRST (newest) page of the infinite cache,
 * deduplicating across all loaded pages — a realtime event can race the
 * optimistic insert, and an older page could already contain the row.
 */
function prependUnique(
	cache: InfiniteData<GetChatMessagesResponse> | undefined,
	message: ChatMessageDTO,
): InfiniteData<GetChatMessagesResponse> | undefined {
	if (!cache || cache.pages.length === 0) return cache
	if (cache.pages.some((p) => p.messages.some((m) => m.id === message.id))) {
		return cache
	}
	const [first, ...rest] = cache.pages
	// Realtime rides unordered Cloud Tasks (ZPL-295): two `created` events can
	// arrive inverted, so the newest page is re-sorted by createdAt (newest
	// first — the list renders reversed) instead of blindly prepended.
	const messages = [message, ...first.messages].sort((a, b) =>
		b.createdAt.localeCompare(a.createdAt),
	)
	return {
		...cache,
		pages: [{ ...first, messages }, ...rest],
	}
}

/**
 * Replace an existing message's row across all loaded pages (ZPL-295) — an
 * in-place content change, today only the approval card's status flip. A
 * message not in the cache is ignored (it will arrive correct on fetch).
 */
function replaceById(
	cache: InfiniteData<GetChatMessagesResponse> | undefined,
	message: ChatMessageDTO,
): InfiniteData<GetChatMessagesResponse> | undefined {
	if (!cache) return cache
	return {
		...cache,
		pages: cache.pages.map((p) => ({
			...p,
			messages: p.messages.map((m) => (m.id === message.id ? message : m)),
		})),
	}
}

export function useChat(
	propertyId: string | undefined,
	chatId: string | undefined,
	projectId?: string,
	contextProjectId?: string,
): UseChatResult {
	const queryClient = useQueryClient()
	const apiBase = projectId
		? `/api/common/properties/${propertyId}/projects/${projectId}`
		: `/api/common/properties/${propertyId}`
	const queryKey =
		propertyId && chatId
			? queryKeys.chatMessages(propertyId, chatId)
			: (["properties", "__nil__", "chats", "__nil__", "messages"] as const)

	const messagesQuery = useInfiniteQuery({
		queryKey,
		queryFn: ({ pageParam }) =>
			apiFetch<GetChatMessagesResponse>(
				`${apiBase}/chats/${chatId}/messages?limit=50${
					pageParam ? `&before=${encodeURIComponent(pageParam)}` : ""
				}`,
			),
		initialPageParam: null as string | null,
		// DESC pages: nextBefore cursors into strictly older messages.
		getNextPageParam: (last) => last.nextBefore,
		enabled: !!propertyId && !!chatId,
	})

	const sendMutation = useMutation({
		mutationFn: (input: { content: string; mentions?: SendMentions }) =>
			apiFetch<PostChatMessageResponse>(`${apiBase}/chats/${chatId}/messages`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					content: input.content,
					...(contextProjectId
						? { context: { projectId: contextProjectId } }
						: {}),
					...(input.mentions && input.mentions.userIds.length > 0
						? { mentionUserIds: input.mentions.userIds }
						: {}),
					...(input.mentions?.mareczek ? { mentionsMareczek: true } : {}),
				}),
			}),
		onSuccess: (res) => {
			queryClient.setQueryData<InfiniteData<GetChatMessagesResponse>>(
				queryKey,
				(cache) => prependUnique(cache, res.message),
			)
		},
	})

	const sendMessage = useCallback(
		async (content: string, mentions?: SendMentions) => {
			if (!propertyId || !chatId) return
			await sendMutation.mutateAsync({ content, mentions })
		},
		[propertyId, chatId, sendMutation],
	)

	let state: ChatState
	if (messagesQuery.isLoading) state = { status: "loading" }
	else if (messagesQuery.error)
		state = {
			status: "error",
			message:
				messagesQuery.error instanceof Error
					? messagesQuery.error.message
					: "Unknown error",
		}
	else if (messagesQuery.data) {
		const messages = messagesQuery.data.pages.flatMap((p) => p.messages)
		state = {
			status: "ready",
			messages,
			threadId: messages[0]?.threadId ?? chatId ?? null,
		}
	} else state = { status: "loading" }

	return {
		state,
		sendMessage,
		loadOlder: () => {
			if (!messagesQuery.isFetchingNextPage) void messagesQuery.fetchNextPage()
		},
		hasOlder: messagesQuery.hasNextPage ?? false,
		loadingOlder: messagesQuery.isFetchingNextPage,
	}
}

/**
 * Patches the `unreadMessageCount` of a single chat in whichever DTO holds
 * the chat rail: the `ProjectDTO` cache when `projectId` is set, otherwise
 * the `PropertyDTO` cache. Both are bare DTOs (their `use*` hooks unwrap the
 * API envelope), so we patch `chats` directly.
 */
function patchUnread(
	queryClient: QueryClient,
	propertyId: string,
	projectId: string | undefined,
	chatId: string,
	update: (current: number) => number,
): void {
	if (projectId) {
		queryClient.setQueryData<ProjectDTO>(
			queryKeys.project(propertyId, projectId),
			(cache) => {
				if (!cache?.chats) return cache
				return {
					...cache,
					chats: cache.chats.map((c) =>
						c.id === chatId
							? { ...c, unreadMessageCount: update(c.unreadMessageCount) }
							: c,
					),
				}
			},
		)
		return
	}
	queryClient.setQueryData<PropertyDTO>(
		queryKeys.property(propertyId),
		(cache) => {
			if (!cache?.chats) return cache
			return {
				...cache,
				chats: cache.chats.map((c) =>
					c.id === chatId
						? { ...c, unreadMessageCount: update(c.unreadMessageCount) }
						: c,
				),
			}
		},
	)
}

/**
 * Folds a realtime event into both the per-chat message cache AND the
 * property's (or project's) `chats[].unreadMessageCount`. Called by both
 * per-user and shared chat channel hooks.
 */
export function applyRealtimeToChatCaches(
	queryClient: QueryClient,
	propertyId: string,
	myUserId: string,
	msg: RealtimeMessage,
	projectId?: string,
): void {
	if (msg.event === "chat-message.created") {
		const m = msg.payload
		// A Mareczek/system reply (any non-user author) ends the thinking
		// indicator for the thread it landed in (ZPL-152: keyed per chat).
		if (m.actorType !== "user") {
			setMareczekThinking(queryClient, m.threadId, false)
		}
		queryClient.setQueryData<InfiniteData<GetChatMessagesResponse>>(
			queryKeys.chatMessages(propertyId, m.threadId),
			(cache) => prependUnique(cache, m),
		)
		const fromOther = !(m.actorType === "user" && m.actorId === myUserId)
		if (fromOther) {
			patchUnread(queryClient, propertyId, projectId, m.threadId, (n) => n + 1)
		}
		return
	}
	if (msg.event === "chat-message.updated") {
		const m = msg.payload
		queryClient.setQueryData<InfiniteData<GetChatMessagesResponse>>(
			queryKeys.chatMessages(propertyId, m.threadId),
			(cache) => replaceById(cache, m),
		)
		return
	}
	if (msg.event === "chat-thread.acknowledged") {
		patchUnread(
			queryClient,
			propertyId,
			projectId,
			msg.payload.chatId,
			() => msg.payload.unreadMessageCount,
		)
		return
	}
}
